import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  switchMap,
  map,
  pluck,
  catchError,
  mergeMap,
  tap,
  delay,
  take,
  takeLast,
  withLatestFrom,
} from 'rxjs/operators';
import { of, Subscription, throwError, Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';

import { getSelectedAudioAsset } from 'app/delivery/delivery-main-screen/store/audio-asset.selectors';
import { HelperService } from 'app/core/services/helper.service';
import { DeliveryAssetService } from 'app/core/services/delivery-asset.service';
import { DownloadFileService } from 'app/core/services/download-file.service';
import { ToastService } from 'app/core/services/toast.service';
import { DeliveryAsset } from 'app/core/models/delivery-asset.model';
import { OfflineDelivery } from 'app/core/models/delivery.model';
import { selectOfflineDelivery } from '../selectors/offline.selectors';
import * as fromActions from '../actions/delivery-assets.actions';
import { selectOfflineState } from '../selectors';

@Injectable()
export class DeliveryAssetEffects {
  actions$ = inject(Actions);
  store = inject(Store);
  deliveryAssetService = inject(DeliveryAssetService);
  downloadFileService = inject(DownloadFileService);
  toastService = inject(ToastService);

  loadAssetDeliveries$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.LoadAsset),
        pluck('payload'),
        switchMap((resp: { deliveryId: string; category: string }) =>
          this.store.pipe(
            select(selectOfflineState),
            switchMap((isOffline: boolean) => {
              let getAssets: Observable<DeliveryAsset[]> = isOffline
                ? this.getCompletedOfflineAudioAssets(resp.deliveryId)
                : this.deliveryAssetService.getDeliveryAssets(resp.deliveryId, resp.category);

              getAssets = this.getAudioAssets(isOffline, resp, getAssets);
              getAssets = this.getHomeAssets({ isOffline, resp, assets$: getAssets });

              return this.loadAssetDeliveriesResult(getAssets, resp.deliveryId, resp.category);
            }),
          ),
        ),
      ),
    { dispatch: true },
  );

  downloading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ToggleDownloading),
      pluck('payload'),
      mergeMap((params: any) => {
        const downloadSubscription = this.downloadFileService
          .downloadFile(params.url, `${params.name}.${params.type}`)
          .pipe(
            tap(() => {
              this.store.dispatch(
                fromActions.DownloadingSuccess({ payload: params.campaignTrackId }),
              );
            }),
            delay(3000),
            tap(() =>
              this.store.dispatch(fromActions.AllowDownload({ payload: params.campaignTrackId })),
            ),
            catchError((err: any) => {
              this.store.dispatch(fromActions.DownloadingFail({ payload: params.campaignTrackId }));
              return throwError(err);
            }),
          )
          .subscribe();

        this.downloads.push({
          campaignTrackId: params.campaignTrackId,
          subscription: downloadSubscription,
        });
        return of();
      }),
    ),
    { dispatch: false }
  );

  cancelDownloading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CancelDownloading),
      pluck('payload'),
      mergeMap((campaignTrackId: string) => {
        const downloadIndex = this.downloads.findIndex(
          (donwload: any) => donwload.campaignTrackId === campaignTrackId,
        );
        const donwloading = this.downloads[downloadIndex];

        if (donwloading) {
          donwloading.subscription.unsubscribe();
          this.store.dispatch(
            fromActions.DownloadingFail({ payload: donwloading.campaignTrackId }),
          );
          this.downloads.splice(downloadIndex, 1);
        }

        return of();
      }),
    ),
    { dispatch: false }
  );

  downloadingCdn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ToggleCdnDownloading),
      pluck('payload'),
      mergeMap((params: any) =>
        this.downloadFileService.getCdnUrl(params.url).pipe(
          map((resp: string) => ({
            success: true,
            url: resp,
            name: params.name,
            type: `.${params.type}`,
            campaignTrackId: params.campaignTrackId,
          })),
          catchError(() => {
            this.store.dispatch(fromActions.AllowDownload({ payload: params.campaignTrackId }));
            return of({
              success: false,
              campaignTrackId: params.campaignTrackId,
            });
          }),
        ),
      ),
      mergeMap((asset: any) => {
        if (asset.success) {
          return this.downloadFileService
            .downloadCdnFile(asset.url, `${asset.name}.${asset.type}`)
            .pipe(
              tap(() =>
                this.store.dispatch(
                  fromActions.DownloadingSuccess({ payload: asset.campaignTrackId }),
                ),
              ),
              delay(3000),
              mergeMap(() => of(fromActions.AllowDownload({ payload: asset.campaignTrackId }))),
              catchError(() => {
                this.store.dispatch(fromActions.AllowDownload({ payload: asset.campaignTrackId }));
                return of(fromActions.DownloadingFail({ payload: asset.campaignTrackId }));
              }),
            );
        }

        return of(fromActions.DownloadingFail({ payload: asset.campaignTrackId }));
      }),
    ),
  );

  downloadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ToggleDownloadAll, fromActions.CancelDownloadAll),
      pluck('payload'),
      switchMap((params: any) => {
        if (params.type === 'cancel_all') {
          this.downloads.forEach((download: any) => download.subscription.unsubscribe());
          return of(true).pipe(
            map(() => {
              this.allowToDownloadAll();
              return fromActions.DownloadingAllFail({ payload: '' });
            }),
          );
        }
        return this.downloadFileService.getAllFiles(params).pipe(
          map(() => {
            this.allowToDownloadAll();
            return fromActions.DownloadingAllSuccess();
          }),
          catchError(() => {
            this.allowToDownloadAll();
            return of(fromActions.DownloadingAllFail({ payload: '' }));
          }),
        );
      }),
    ),
  );

  downloadAllFormats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.ToggleDownloadAllFormats),
      pluck('payload'),
      switchMap((params: any) => {
        const downloadSubscription = this.downloadFileService
          .getAllFiles(params)
          .pipe(
            takeLast(1),
            tap(() =>
              this.store.dispatch(
                fromActions.DownloadingSuccess({ payload: params.campaignTrackId }),
              ),
            ),
            delay(3000),
            tap(() => this.store.dispatch(fromActions.AllowDownloadAll())),
            catchError((err: any) => {
              this.store.dispatch(fromActions.AllowDownloadAll());
              this.store.dispatch(fromActions.DownloadingFail({ payload: params.campaignTrackId }));
              return throwError(err);
            }),
          )
          .subscribe();

        this.downloads.push({
          campaignTrackId: params.campaignTrackId,
          subscription: downloadSubscription,
        });
        return of();
      }),
    ),
    { dispatch: false }
  );

  selectAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.SetSelectedAsset),
      pluck('payload'),
      tap((asset: DeliveryAsset) => {
        if (this.isMediaAsset(asset) && !this.deliveryAssetService.isStreamAsset(asset)) {
          this.showStreamingError();
        }
      }),
      mergeMap(() => of()),
    ),
    { dispatch: false }
  );

  loadMoreAsset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.LoadMoreAsset),
      pluck('payload'),
      mergeMap((resp: { deliveryKeyCode: string; category: string }) => {
        const { deliveryKeyCode, category } = resp;

        return this.deliveryAssetService.getDeliveryAssets(deliveryKeyCode, category).pipe(
          map((assets: DeliveryAsset[] = []) =>
            fromActions.LoadMoreAssetSuccess({
              payload: {
                assets,
              },
            }),
          ),
          catchError(() => of(fromActions.LoadMoreAssetFail())),
        );
      }),
    ),
  );

  private downloads: { campaignTrackId: string; subscription: Subscription }[] = [];

  private allowToDownloadAll(): void {
    setTimeout(() => {
      this.store.dispatch(fromActions.AllowDownloadAll());
    }, 3000);
  }

  private isMediaAsset(asset: DeliveryAsset): boolean {
    return asset.mediaType === 'AUDIO' || asset.mediaType === 'VIDEO';
  }

  private showStreamingError(): void {
    // tslint:disable-next-line: max-line-length
    const errorMsg =
      'This is a download-only track. Please use Promo Portal on your computer to download it.';
    this.toastService.showToast(errorMsg);
  }

  private getAudioAssets(isOffline, resp, assets$) {
    if (isOffline && resp.category === 'audio') {
      return assets$.pipe(
        mergeMap((assets: DeliveryAsset[]) =>
          this.getOfflineAudioAssets(resp.deliveryId).pipe(
            map((offlineAssets: DeliveryAsset[]) =>
              offlineAssets && offlineAssets.length ? offlineAssets : assets,
            ),
          ),
        ),
      );
    }
    return assets$;
  }

  private getOfflineAudioAssets(deliveryKeyCode): Observable<DeliveryAsset[]> {
    return this.store.pipe(
      select(selectOfflineDelivery, { deliveryKeyCode }),
      take(1),
      map((offlineDelivery: OfflineDelivery) =>
        offlineDelivery.offlineStatus === 'downloaded' ? offlineDelivery.offlineAudioAssets : [],
      ),
    );
  }

  private getCompletedOfflineAudioAssets(deliveryKeyCode): Observable<DeliveryAsset[]> {
    return this.store.pipe(
      select(selectOfflineDelivery, { deliveryKeyCode }),
      take(1),
      map((offlineDelivery: OfflineDelivery) =>
        offlineDelivery.offlineStatus === 'downloaded'
          ? offlineDelivery.offlineAudioAssets.filter(
              (asset: DeliveryAsset) => asset.offlineDownloadStatus === 'COMPLETED',
            )
          : [],
      ),
    );
  }

  private getHomeAssets({ resp, assets$, isOffline }): Observable<DeliveryAsset[]> {
    const { category } = resp;

    if (isOffline || category !== 'home') {
      return assets$;
    }

    // TODO: here implement offline audio if needed

    return assets$;
  }

  private loadAssetDeliveriesResult(
    getAssets,
    deliveryKeyCode = '',
    category = '',
  ): Observable<any> {
    return getAssets.pipe(
      withLatestFrom(this.store.pipe(select(getSelectedAudioAsset))),
      map(([assets, selectedAudioAsset]) => {
        const deliveryAssets: DeliveryAsset[] = this.isAudioCategory(category)
          ? this.setSelectedAsset(assets, selectedAudioAsset)
          : assets;

        return fromActions.SetAssetState({
          deliveryKeyCode,
          payload: {
            assets: deliveryAssets,
            isPlayingAll: false,
            isDefaultView: true,
            isDownloadingAll: false,
            isInfoView: true,
          },
        });
      }),
      catchError((error: any) => of(fromActions.LoadAssetFail({ payload: error }))),
    );
  }

  private isAudioCategory(category): boolean {
    return category === 'home' || category === 'audio';
  }

  private setSelectedAsset(
    assets: DeliveryAsset[],
    selectedAudioAsset: DeliveryAsset,
  ): DeliveryAsset[] {
    if (!selectedAudioAsset) {
      return assets;
    }

    return assets.map((deliveryAsset: DeliveryAsset) => {
      const modifedAsset = HelperService.cloneDeliveryAsset(deliveryAsset);

      if (modifedAsset?.campaignTrackId === selectedAudioAsset?.campaignTrackId) {
        modifedAsset.isSelected = true;
        modifedAsset.isPlaying = selectedAudioAsset.isPlaying;
        modifedAsset.isReadyToPlay = selectedAudioAsset.isReadyToPlay;
      }

      return modifedAsset;
    });
  }
}
